import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Heart, Star, Plus, Minus, Share2, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const ProductDetailModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const { showToast } = useToast();

  if (!product) return null;

  const getCategoryName = () => {
    if (!product.category) return '';
    if (typeof product.category === 'string') return product.category;
    if (typeof product.category === 'object' && product.category.name) return product.category.name;
    return '';
  };

  const getProductImages = () => {
    const mainImage = product.image_url || product.image || `https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=600&fit=crop&q=80`;
    return [mainImage, mainImage, mainImage]; // يمكنك إضافة صور أخرى هنا
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      showToast('Please login to add items to cart', 'error');
      return;
    }

    setIsAddingToCart(true);
    try {
      const result = await addToCart(product.id, quantity);
      showToast(result.message, 'success');
    } catch (error) {
      showToast(error.message || 'Failed to add item to cart', 'error');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.8, y: 50 }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={onClose}
          />
          
            {/* Modal */}
            <motion.div
              className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              style={{ maxWidth: '95vw' }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 z-10 p-2 rounded-full bg-white/90 text-gray-700 hover:bg-white hover:text-gray-900 transition-all duration-300 shadow-lg"
              >
                <X size={24} />
              </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
              {/* Product Images */}
              <div className="space-y-4">
                <motion.div
                  className="aspect-square bg-gray-100 rounded-2xl overflow-hidden"
                  whileHover={{ scale: 1.02 }}
                >
                  <img
                    src={getProductImages()[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                
                {/* Thumbnail Images */}
                <div className="grid grid-cols-3 gap-4">
                  {getProductImages().map((image, index) => (
                    <motion.button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                        selectedImage === index ? 'border-blue-500' : 'border-gray-200'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                {/* Category */}
                {getCategoryName() && (
                  <motion.span
                    className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 text-sm font-bold uppercase tracking-wider rounded-full"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {getCategoryName()}
                  </motion.span>
                )}

                {/* Product Name */}
                <motion.h1
                  className="text-4xl font-bold text-gray-900"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {product.name}
                </motion.h1>

                {/* Rating */}
                <motion.div
                  className="flex items-center space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        className={`${
                          i < (product.rating || 4) 
                            ? 'text-yellow-400 fill-current' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-gray-600 font-medium">
                    ({product.reviews_count || Math.floor(Math.random() * 100)} reviews)
                  </span>
                </motion.div>

                {/* Price */}
                <motion.div
                  className="flex items-center space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <span className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    ${Number(product.price).toFixed(2)}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-xl text-gray-500 line-through">
                      ${Number(product.original_price).toFixed(2)}
                    </span>
                  )}
                </motion.div>

                {/* Description */}
                <motion.p
                  className="text-gray-600 text-lg leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  {product.description}
                </motion.p>

                {/* Stock Status */}
                {product.stock !== undefined && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    {product.stock > 0 ? (
                      <span className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 font-semibold rounded-full">
                        ✅ In Stock ({product.stock} available)
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 font-semibold rounded-full">
                        ❌ Out of Stock
                      </span>
                    )}
                  </motion.div>
                )}

                {/* Quantity Selector */}
                <motion.div
                  className="flex items-center space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  <span className="text-lg font-semibold text-gray-900">Quantity:</span>
                  <div className="flex items-center space-x-3 bg-gray-100 rounded-2xl p-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300 text-black"
                    >
                      <Minus size={16} color="black" />
                    </button>
                    <span className="w-12 text-center font-bold text-lg text-black">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-300 text-black"
                    >
                      <Plus size={16} color="black" />
                    </button>
                  </div>
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  className="flex space-x-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart || (product.stock !== undefined && product.stock <= 0)}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
                  >
                    {isAddingToCart ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart size={24} />
                        <span>Add to Cart</span>
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => setIsLiked(!isLiked)}
                    className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                      isLiked 
                        ? 'bg-red-500 border-red-500 text-white' 
                        : 'bg-white border-gray-300 text-gray-700 hover:border-red-500'
                    }`}
                  >
                    <Heart size={24} fill={isLiked ? 'currentColor' : 'none'} />
                  </button>

                  <button className="p-4 rounded-2xl border-2 border-gray-300 text-gray-700 hover:border-blue-500 transition-all duration-300">
                    <Share2 size={24} />
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductDetailModal;
